import { SnackbarOrigin } from '@quillbot/design-system';
import { IPlatform } from 'core/models/platform';
import { IpData } from 'src/authentication/models/api';
import {
  IQuillDrawer,
  Recaptcha,
  KeyboardToggle,
  MaintenanceNotification,
  ExtensionMetaData,
  ABTestRecord,
  ABTestInfoRecord,
} from '../models/redux/store';
import * as commonActionTypes from '../models/redux/actions';
import * as actionNames from '../models/redux/actions/names';
import track from '../handlers/track';
import { QUILLPRODUCTS } from '@Common/models';

export const toggleDrawerAction = (drawerName: IQuillDrawer): commonActionTypes.ToggleDrawerAction => ({
  type: actionNames.UI_TOGGLE_DRAWER,
  payload: drawerName,
});

export const setTheme = (themeName: string): commonActionTypes.SetTheme => ({
  type: actionNames.SET_THEME,
  payload: themeName,
});

export const toggleCompareDrawerAction = (drawerOpen: boolean): commonActionTypes.ToggleCompareDrawerAction => {
  track.clickedCompareMode(drawerOpen);
  return {
    type: actionNames.COMPARE_TOGGLE_DRAWER,
    payload: drawerOpen,
  };
};

export const toggleSignUpModel = (isOpenSignUpModel: boolean): commonActionTypes.ToggleSignUpModel => ({
  type: actionNames.TOGGLE_SIGNUP_MODEL,
  payload: isOpenSignUpModel,
});

export const setPlatformAction = (platform: IPlatform): commonActionTypes.SetPlatformAction => ({
  type: actionNames.SET_PLATFORM,
  payload: platform,
});
export const setLanguageAction = (language: string): commonActionTypes.SetLanguageAction => ({
  type: actionNames.SET_LANGUAGE,
  payload: language,
});

export const toggleToastrAction = (
  message?: string,
  timeout?: number,
  isShown = true,
  position = { vertical: 'bottom', horizontal: 'left' } as SnackbarOrigin
): commonActionTypes.ToggleToastAction => ({
  type: actionNames.UI_TOGGLE_TOAST,
  payload: {
    message,
    timeout,
    isShown,
    position,
  },
});

export const toggleAlertDialogAction = (
  isBackdropDismissalAllowed: boolean,
  content: string | string[],
  title: string,
  isShown: boolean,
  buttonLabel?: string,
  isErrorModal?: boolean,
  titleIcon?: string
): commonActionTypes.ToggleAlertDialog => ({
  type: actionNames.UI_SHOW_ALERT,
  payload: {
    isBackdropDismissalAllowed,
    content,
    title,
    isShown,
    buttonLabel,
    isErrorModal,
    titleIcon,
  },
});

export const toggleRecaptchaAction = (payload: Recaptcha): commonActionTypes.ToggleRecaptchaAction => ({
  type: actionNames.UI_TOGGLE_RECAPTCHA,
  payload,
});

export const toggleSessionDialogAction = (payload: Recaptcha): commonActionTypes.ToggleSessionDialogAction => ({
  type: actionNames.UI_TOGGLE_SESSION_DIALOG,
  payload,
});

export const toggleKeyboardAction = (
  isOpen: boolean,
  elementName?: KeyboardToggle['element']
): commonActionTypes.ToggleKeyboardAction => ({
  type: actionNames.UI_TOGGLE_KEYBOARD,
  payload: {
    isOpen,
    element: elementName,
  },
});

export const setExtensionAction = (extensionMetaData: ExtensionMetaData): commonActionTypes.SetExtensionAction => ({
  type: actionNames.SET_EXTENSION_MODE,
  payload: extensionMetaData,
});

export const setMaintenanceAction = (payload: MaintenanceNotification): commonActionTypes.SetMaintenanceAction => ({
  type: actionNames.MAINTENANCE_MODE,
  payload,
});

export const setExportedImages = (images: string[]): commonActionTypes.SetExportedImages => ({
  type: actionNames.SET_EXPORTED_IMAGES,
  payload: images,
});

export const setIpData = (ipData: IpData): commonActionTypes.SetIpData => ({
  type: actionNames.SET_IP_DATA,
  payload: ipData,
});

export const setParaphraserDataAction = (text: string): commonActionTypes.SetParaphraserDataAction => ({
  type: actionNames.PARAPHRASER_RECEIVED_OTHER_PRODUCT_DATA,
  payload: {
    text,
  },
});

export const setSignupModalState = (show: boolean): commonActionTypes.SetSignupModalState => ({
  type: actionNames.SET_SIGN_UP_MODAL_STATE,
  payload: show,
});

export const setCountryCode = (countryCode: string): commonActionTypes.SetCountryCode => ({
  type: actionNames.SET_COUNTRY_CODE,
  payload: countryCode,
});

export const setIndependentTool = (independentToolFlag: boolean): commonActionTypes.SetIndependentTool => ({
  type: actionNames.SET_INDEPENDENT_TOOL,
  payload: independentToolFlag,
});

export const setRegionalLanguage = (language: string): commonActionTypes.SetRegionalLanguage => ({
  type: actionNames.SET_REGIONAL_LANGUAGE,
  payload: language,
});

export const setSpamCheckCallStatus = (status: boolean): commonActionTypes.SetSpamCheckCallStatus => ({
  type: actionNames.SET_SPAM_CHECK_CALL_STATUS,
  payload: status,
});

export const setSidebarStateChange = (pinned: boolean): commonActionTypes.SetSidebarPinned => ({
  type: actionNames.SET_SIDEBAR_PINNED,
  payload: pinned,
});

export const setSidebarPinnned =
  (pinned: boolean, source = 'system') =>
  (dispatch: any) => {
    track.sideBarStateChangeAction(pinned, actionNames.SET_SIDEBAR_PINNED, source);
    dispatch(setSidebarStateChange(pinned));
  };

export const setPartnerCompany = (partnerCompany: string): commonActionTypes.SetPartnerCompany => ({
  type: actionNames.SET_PARTNER_COMPANY,
  payload: partnerCompany,
});

export const setABExperiments = ({
  abExperiments,
  abExperimentsInfo,
}: {
  abExperiments: ABTestRecord;
  abExperimentsInfo: ABTestInfoRecord;
}): commonActionTypes.SetABExperiments => ({
  type: actionNames.SET_AB_EXPERIMENTS,
  payload: {
    abExperiments,
    abExperimentsInfo,
  },
});

export const setAppLangauge = (language: string): commonActionTypes.SetAppLanguage => ({
  type: actionNames.SET_APP_LANGUAGE,
  payload: language,
});

export const setCrossProductUpsell = (product: QUILLPRODUCTS): commonActionTypes.SetCrossProductUpsell => ({
  type: actionNames.SET_CROSS_PRODUCT_UPSELL,
  payload: product,
});

export const hideSessionManager = (value: boolean): commonActionTypes.HideSessionManager => ({
  type: actionNames.HIDE_SESSION_MANAGER,
  payload: value,
});

export const userClickedThemeBtn = (value: boolean): commonActionTypes.UserClickedThemeBtn => ({
  type: actionNames.USER_CLICKED_THEME_BTN,
  payload: value,
});

export const userClickedLogOutBtn = (value: boolean): commonActionTypes.UserClickedLogOutBtn => ({
  type: actionNames.USER_CLICKED_LOGOUT_BTN,
  payload: value,
});

export const setCampaignAction = (campaign: string): commonActionTypes.SetCampaignAction => ({
  type: actionNames.SET_CAMPAIGN,
  payload: campaign,
});
